/**
 * Centralized session status — single source of truth for session UI.
 *
 * All trigger paths (sessionManager, SecureAuthService, DashboardBootstrap,
 * SocketClient) converge here via `setSessionStatus()`.
 *
 * React components consume this via `useSyncExternalStore` with
 * `subscribeSessionStatus` / `getSessionStatusSnapshot`.
 *
 * NOT in Redux — auth state shouldn't be serialized or replayed.
 */

export type SessionStatus = 'active' | 'expired' | 'replaced';

let status: SessionStatus = 'active';
const listeners = new Set<() => void>();

/**
 * Transition session status. Idempotent — only the first transition
 * from 'active' matters. Subsequent calls are no-ops.
 */
export function setSessionStatus(next: SessionStatus): void {
  if (status !== 'active') return; // Already transitioned — ignore
  if (next === 'active') return;   // No-op
  status = next;
  listeners.forEach(fn => fn());
}

/** Snapshot for `useSyncExternalStore` */
export function getSessionStatusSnapshot(): SessionStatus {
  return status;
}

/** Subscribe callback for `useSyncExternalStore` */
export function subscribeSessionStatus(callback: () => void): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}
